import React, { useMemo, useState } from 'react';
import { FaExternalLinkAlt, FaUndo } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { getModule } from '../F22/registry';
import { rollbackRenta } from '../../../services/declaracionRenta/f22Service';
import CertificadoPagadoButton from '../CertificadoPagadoButton';
import { useAuth } from '../../../context/AuthContext';
import '../../../styles/components/F22Form.css';

const noop = () => {};

/**
 * Vista de solo lectura de una renta ya calculada. No recalcula nada:
 * reconstruye `data` desde la fila guardada y reusa las mismas secciones del
 * formulario en modo lectura.
 *
 * Todo lo del certificado de renta pagada —ficha del PDF, códigos extraídos,
 * comparación contra lo calculado, eliminarlo— vive en su propia página
 * (CertificadoRentaPagadaPage); acá solo queda el botón que lleva a ella.
 */
const RentaSavedView = ({ anioTributario, regimen, row, pagado, onRecargar }) => {
  const { selectedCompany } = useAuth();
  const idCompany = selectedCompany?.id_company || selectedCompany?.id;
  const mod = useMemo(() => getModule(anioTributario, regimen), [anioTributario, regimen]);

  const SECTIONS = mod?.SECTIONS || [];
  const [activeSection, setActiveSection] = useState(SECTIONS[0]?.id || '');
  const [rectificando, setRectificando] = useState(false);

  const data = mod?.rowToState ? mod.rowToState(row) : null;
  const computed = data && mod?.recalculate ? mod.recalculate(data) : {};

  const currentSection = SECTIONS.find((s) => s.id === activeSection);
  const ActiveComponent = currentSection ? mod[currentSection.component] : null;

  const handleRectificar = async () => {
    const confirm = await Swal.fire({
      title: '¿Rectificar esta declaración?',
      html: `Se eliminarán los datos calculados, el PDF y los asientos contables asociados${
        pagado ? ', <strong>y también el certificado de renta pagada cargado</strong>' : ''
      }, para poder recalcular desde cero. Esta acción no se puede deshacer.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, rectificar',
      cancelButtonText: 'Cancelar',
    });
    if (!confirm.isConfirmed) return;

    setRectificando(true);
    try {
      const result = await rollbackRenta(idCompany, anioTributario, regimen);
      if (result.warnings?.length) {
        await Swal.fire('Rectificada con avisos', result.warnings.join('<br/>'), 'warning');
      } else {
        await Swal.fire('Listo', 'La declaración fue rectificada.', 'success');
      }
      if (onRecargar) onRecargar();
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'No se pudo rectificar la declaración', 'error');
    } finally {
      setRectificando(false);
    }
  };

  return (
    <div className="renta-saved-view">
      <div className="renta-saved-actions">
        {row.pdf_url && (
          <a href={row.pdf_url} target="_blank" rel="noopener noreferrer" className="renta-saved-btn renta-saved-btn-primary">
            <FaExternalLinkAlt /> Ver PDF
          </a>
        )}
        <CertificadoPagadoButton
          anioTributario={anioTributario}
          regimen={regimen}
          pagado={pagado}
          onCargado={onRecargar}
        />
        <button className="renta-saved-btn renta-saved-btn-danger" onClick={handleRectificar} disabled={rectificando}>
          <FaUndo /> {rectificando ? 'Rectificando...' : 'Rectificar'}
        </button>
      </div>

      {data && (
        <div className="f22-page">
          <div className="f22-nav">
            {SECTIONS.map((section) => (
              <button
                key={section.id}
                className={`f22-nav-tab ${activeSection === section.id ? 'active' : ''}`}
                onClick={() => setActiveSection(section.id)}
              >
                {section.label}
              </button>
            ))}
          </div>
          <div className="f22-content">
            {ActiveComponent && (
              <ActiveComponent
                data={data}
                computed={computed}
                updateField={noop}
                updateNestedField={noop}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RentaSavedView;
